// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useScript } from "@awc/App/Hooks/useScript";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

// XNG-Changes
type OwnProps = {
    showAttributesTextLabel?: boolean;
    showDownloadAttributesButton?: boolean;
};

type Props = WidgetProps & HasProduct & OwnProps;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Changes
    attrTextLabel?: TypographyPresentationProps;
    rowWrapper?: InjectableCss;
    downloadButton?: ButtonPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;

export const AttributeBlock: React.FC<{ attributeTypes?: AttributeTypeModel[] }> = ({ attributeTypes = [] }) => {
    return (
        <>
            {attributeTypes.map(attributeType => (
                <StyledWrapper
                    key={attributeType.id}
                    data-test-selector="attributes_item"
                    data-attributetypeid={attributeType.id}
                    {...styles.rowWrapper}
                    className="spec-attributes"
                >
                    <Typography
                        data-test-selector="attributes_item_label"
                        {...styles.attributeTypeLabelText}
                        className="attr-row-label"
                    >
                        {attributeType.label || attributeType.name}
                    </Typography>
                    <Typography
                        {...styles.attributeTypeValuesText}
                        as="p"
                        data-test-selector="attributes_item_value"
                        className="value attr-row-value"
                    >
                        {[...(attributeType.attributeValues || [])]
                            .sort((a, b) => {
                                const orderCompare = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
                                return orderCompare !== 0 ? orderCompare : a.valueDisplay.localeCompare(b.valueDisplay);
                            })
                            .map(attributeValue => attributeValue.valueDisplay)
                            .join(", ")}
                    </Typography>
                </StyledWrapper>
            ))}
        </>
    );
};

const src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/1.5.3/jspdf.min.js";

const ProductDetailsAttributes: React.FC<Props> = ({
    product: { brand, attributeTypes, productTitle },
    showAttributesTextLabel = true,
    showDownloadAttributesButton = true,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const status = useScript(src);

    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const collectDivData = () => {
        if (!containerRef.current) {
            return;
        }
        const rows = containerRef.current.querySelectorAll(".spec-attributes");
        const data = [["Attributes", "Values"]];

        rows.forEach((row: any) => {
            const label = row.querySelector(".attr-row-label")?.innerText.trim() || "";
            const value = row.querySelector(".attr-row-value")?.innerText.trim() || "";
            data.push([label, value]);
        });

        return data;
    };

    const generatePDF = () => {
        if (!window || !(window as any).jsPDF) {
            return;
        }

        const jsPDF = (window as any).jsPDF;

        if (!jsPDF) {
            return;
        }

        // eslint-disable-next-line new-cap
        const doc = new jsPDF("p", "pt", "letter");
        const tableData = collectDivData();
        const productName = productTitle;

        doc.setProperties({ title: "Product Attributes" });
        doc.text(productName, 25, 50);
        doc.cellInitialize();

        const hardMinKeyLength = 120;
        const hardMinValueLength = 180;
        const hardMaxKeyLength = 200;
        const hardMaxValueLength = 300;

        let maxKeyLength = 0;
        let maxValueLength = 0;

        tableData?.forEach(([k, v]) => {
            maxKeyLength = Math.max(maxKeyLength, k.length);
            maxValueLength = Math.max(maxValueLength, v.length);
        });

        const cellWidthKey = Math.min(Math.max(Math.ceil(maxKeyLength * 5.5), hardMinKeyLength), hardMaxKeyLength);
        const cellWidthValue = Math.min(
            Math.max(Math.ceil(maxValueLength * 5.5), hardMinValueLength),
            hardMaxValueLength,
        );

        const leftMargin = 25;
        const topMargin = 75;
        const headerRowHeight = 18;
        const contentRowHeight = 13;

        tableData?.forEach((row, rowIndex) => {
            row.forEach((cellText, colIndex) => {
                const isHeader = rowIndex === 0;
                doc.setFont("helvetica");
                doc.setFontType(isHeader ? "bold" : "normal");
                doc.setFontSize(isHeader ? 11 : 10);

                const width = colIndex === 0 ? cellWidthKey : cellWidthValue;
                const height = isHeader ? headerRowHeight : contentRowHeight;

                doc.cell(leftMargin, topMargin, width, height, cellText, rowIndex);
            });
        });

        let fileName = `AWC-ProductAttributes-${productName}`.replace(/[/\\?%*:|"<>]/g, "-");
        doc.save(`${fileName}.pdf`);
    };

    // XNG-Changes: Classic Attributes sorting and filtering
    const condAttributes = attributeTypes?.filter(attr => attr.label === "No. of Cond.");

    const otherAttributes = attributeTypes
        ?.filter(attr => attr.label !== "No. of Cond.")
        ?.sort((a, b) => {
            const aLabel = a.label || a.name;
            const bLabel = b.label || b.name;
            return aLabel.localeCompare(bLabel);
        });

    return (
        <>
            {showAttributesTextLabel && <Typography {...styles.attrTextLabel}>{translate("Attributes")}</Typography>}
            <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes" ref={containerRef}>
                {/* XNG-Changes: no brand in classic */}
                {/* {brand && (
                    <StyledWrapper data-test-selector="brand_item" {...styles.rowWrapper} className="spec-attributes">
                        <Typography
                            data-test-selector="brand_item_label"
                            {...styles.attributeTypeLabelText}
                            className="attr-row-label"
                        >
                            {translate("Brand")}
                        </Typography>
                        <Typography
                            {...styles.attributeTypeValuesText}
                            data-test-selector="brand_item_value"
                            className="value attr-row-value"
                        >
                            {brand.name}
                        </Typography>
                    </StyledWrapper>
                )} */}
                <AttributeBlock attributeTypes={condAttributes} />
                <AttributeBlock attributeTypes={otherAttributes} />
            </StyledWrapper>
            {showDownloadAttributesButton && (
                <Button onClick={generatePDF} {...styles.downloadButton}>
                    {translate("Download Attributes Table")}
                </Button>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
